'use client'

import { Box, Typography } from '@mui/material'
import { useLocale } from 'next-intl'
import Image from 'next/image'
import React, { createContext, useLayoutEffect, useRef, useState } from 'react'
import tell from './tell.png'

const BADGE_GAP = 8

export interface CheapestQuickestBadgeLayout {
    left: number
    height: number
}

export const CheapestQuickestBadgeWidthContext = createContext<CheapestQuickestBadgeLayout>({
    left: 0,
    height: 0
})

interface CheapestQuickestCardLabelProps {
    children: React.ReactNode
    label: string
    isActive: boolean
    sortOptionId: string
    isMobile: boolean
    isCombined?: boolean
}

const CHEAPEST_COLOR = '#0FA670'
const QUICKEST_COLOR = '#3B82F6'

const colorMap = {
    cheapest: CHEAPEST_COLOR,
    quickest: QUICKEST_COLOR
}


const CheapestQuickestNew: React.FC<CheapestQuickestCardLabelProps> = ({
    children,
    isActive,
    label,
    sortOptionId,
    isMobile,
    isCombined
}) => {
    const locale = useLocale()
    const isArabic = locale === 'ar'
    const badgeRef = useRef<HTMLDivElement>(null)
    const [badgeLayout, setBadgeLayout] = useState<CheapestQuickestBadgeLayout>({ left: 0, height: 0 })

    const measureBadge = React.useCallback(() => {
        if (badgeRef.current) {
            const { offsetWidth, offsetHeight } = badgeRef.current
            setBadgeLayout({
                left: offsetWidth > 0 ? offsetWidth - 10 + BADGE_GAP : 0,
                height: offsetHeight
            })
        }
    }, [])

    useLayoutEffect(() => {
        measureBadge()
        const el = badgeRef.current
        if (!el) return
        const observer = new ResizeObserver(measureBadge)
        observer.observe(el)
        return () => observer.disconnect()
    }, [label, isCombined, locale, measureBadge])

    const ids = isCombined ? sortOptionId.split('_') : [sortOptionId]
    const labels = isCombined ? label.split('_') : [label]

    return (
        <CheapestQuickestBadgeWidthContext.Provider value={badgeLayout}>
            <Box position={"relative"} >
                <Image src={tell} alt='tell' width={10} height={10} style={{
                    position: "absolute",
                    top: badgeLayout.height,
                    ...(isArabic ? { right: -10 } : { left: -10 }),
                    transform: isArabic ? 'scaleX(-1)' : 'none',

                    zIndex: 10,
                }} />
                <Box
                    ref={badgeRef}
                    display={'flex'}
                    justifyContent={'flex-start'}
                    position={"absolute"}
                    top={0}
                    left={-10}
                    zIndex={10}
                    gap={1}
                >
                    {ids?.map((id, index) => {
                        return (
                            <Box
                                key={id}
                                bgcolor={colorMap[id as keyof typeof colorMap]}
                                // width={'100px'}
                                px={1}
                                sx={{
                                    borderRadius: "4px",
                                    ...(index === 0 && { borderBottomLeftRadius: 0 }),
                                }}
                            >
                                <Typography variant="body2" color={'white'} textAlign={'center'} fontWeight={600}>
                                    {labels[index]}
                                </Typography>
                            </Box>
                        )
                    })}
                </Box>

                <Box
                    borderRadius={isMobile ? 2 : 1}
                >
                    {children}
                </Box>
            </Box>
        </CheapestQuickestBadgeWidthContext.Provider>
    )
}

export default CheapestQuickestNew
